import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { hasPagePermissions } from 'hooks/hasPagePermissions'
import { usePermissions } from 'hooks/usePermissions'
import { useMemo, useState } from 'react'
import { ButtonGroup } from 'stories/components'

import { BorrowerPortalMessageThread } from '../BorrowerPortal'
import { TextMessageThread } from '../TextMessages'

/**
 * Sub tabs, in render order. Text Messages leads because it is the conversation a servicer is
 * usually chasing; the keys match the menu keys the two threads used to have screens of their own
 * under, so a `?menu=borrowerPortal` link still names the tab it should land on.
 */
const MESSAGES_TABS: Record<string, string> = {
  textMessages: 'Text Messages',
  borrowerPortal: 'Borrower Portal',
}

/**
 * Servicing > Messages: every conversation with this loan's borrower, one tab per channel.
 *
 * Texting and the borrower portal's secure messages were two menu entries saying the same kind of
 * thing in two places, so a servicer had to know which channel a borrower had used before they
 * could find the message. They are one screen now.
 *
 * Each tab keeps what gated the screen it came from, so merging the two does not widen access:
 * texting needs `Loan.Servicing.TextMessages`, and the portal thread, which never had a permission
 * of its own, still answers to a Page Permissions deny on its old `borrowerPortal` menu key. The
 * screen itself takes no permission - gating it on texting would withhold portal messages from
 * everyone who cannot text.
 */
export default function Messages({ initialTab }: { initialTab?: string }) {
  const { hasPermission } = usePermissions()
  const [requestedTab, setRequestedTab] = useState(initialTab || '')

  const tabs = useMemo(() => {
    const available: Record<string, string> = {}
    // Matches `buildServicingMenu`, which also treats "no opinion yet" as not permitted.
    if (hasPermission('Loan.Servicing.TextMessages')) available.textMessages = MESSAGES_TABS.textMessages
    // The key the menu entry used to be hidden under, so an admin who hid Borrower Portal keeps it
    // hidden here rather than having it reappear as a tab.
    if (hasPagePermissions(['pipeline', 'servicing', 'borrowerPortal']))
      available.borrowerPortal = MESSAGES_TABS.borrowerPortal
    return available
  }, [hasPermission])

  // Derived rather than stored: permissions resolve after the first render, so a tab chosen then
  // would strand a servicer who may text on the portal tab. A tab they click wins over the default.
  const tab = tabs[requestedTab] ? requestedTab : Object.keys(tabs)[0] || ''

  return (
    <div className="p-2 md:p-4">
      <div className="flex items-center gap-2 text-[16px] font-bold text-gray-900 border-b pb-1 mb-4 border-gray-200">
        <ChatBubbleLeftRightIcon className="w-5 h-5 text-shade-blue" />
        Messages
      </div>

      {tab ? (
        <div className="mb-4">
          <ButtonGroup title={tabs} value={tab} onChange={(value: string) => setRequestedTab(value)} />
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500">No message channel is available on this loan.</div>
      )}

      {tab === 'textMessages' && <TextMessageThread />}
      {tab === 'borrowerPortal' && <BorrowerPortalMessageThread />}
    </div>
  )
}
